import { faker } from '@faker-js/faker';
import {
  type DefaultBodyType,
  HttpResponse,
  type HttpResponseResolver,
  type PathParams,
} from 'msw';

import type { AdminUser, SearchRequest, SortDirection } from '~/api/types';
import { ApiStatus } from '~/constants/ApiStatus';
import { decodeJwt } from '~/features/auth/jwt';
import { getSessionObject, setSessionObject } from '~/utils/storage';

import {
  getFakeAdminUsersData,
  getFakeDepartmentData,
  getFakeIDNData,
} from './api/data';
import { adminUserFactory } from './api/factories/admin';
import { departmentDetailsFactory } from './api/factories/department';
import { IDNFactory } from './api/factories/IDN';
import { STORAGE_KEYS } from './storageKeys';

const getUserFromAuthHeader = (authHeader?: string): AdminUser | null => {
  if (!authHeader?.startsWith('Bearer ')) return null;
  const token = authHeader.substring('Bearer '.length);

  try {
    const { userID, idnID, departmentID } = decodeJwt(token);

    if (!userID) {
      return null;
    }

    const adminUsers = getFakeAdminUsersData();

    const user = adminUsers.find((u) => u.id === userID);

    if (!user) {
      let idn = getFakeIDNData().find((i) => i.id === idnID);

      if (!idn) {
        idn = IDNFactory({ id: idnID });
        setPersistSessionObject(STORAGE_KEYS.IDNS, [...getFakeIDNData(), idn]);
      }

      let department = getFakeDepartmentData().find(
        (d) => d.id === departmentID,
      );

      if (!department) {
        department = departmentDetailsFactory({
          idns: getFakeIDNData(),
          overrides: { id: departmentID },
        });
        setPersistSessionObject(STORAGE_KEYS.DEPARTMENTS, [
          ...getFakeDepartmentData(),
          department,
        ]);
      }

      const newUser = adminUserFactory({
        departmentDetails: getFakeDepartmentData(),
        overrides: {
          id: userID,
          mainDepartment: department,
          departments: [department],
        },
      });

      setPersistSessionObject(STORAGE_KEYS.ADMIN_USERS, [
        ...getFakeAdminUsersData(),
        newUser,
      ]);

      return newUser;
    }

    return user;
  } catch (error) {
    console.error('Failed to decode JWT', token, error);

    return null;
  }
};

export function withAuth<
  Params extends PathParams,
  RequestBodyType extends DefaultBodyType,
  ResponseBodyType extends DefaultBodyType,
>(
  resolver: (
    info: Parameters<
      HttpResponseResolver<Params, RequestBodyType, ResponseBodyType>
    >[0] & {
      user: AdminUser;
    },
  ) => ReturnType<
    HttpResponseResolver<Params, RequestBodyType, ResponseBodyType>
  >,
): HttpResponseResolver<Params, RequestBodyType, ResponseBodyType> {
  // @ts-expect-error Early return type inference
  return (info) => {
    const { request } = info;
    const authHeader = request.headers.get('Authorization') ?? undefined;

    const user = getUserFromAuthHeader(authHeader);

    if (!user) {
      return HttpResponse.json(
        { error: 'Unauthorized' },
        { status: ApiStatus.UNAUTHORIZED },
      );
    }

    return resolver({
      ...info,
      user,
    });
  };
}

export const sanitizePhone = (phone?: string): string =>
  phone?.replace(/\s+/g, '') ?? '';

export const compareStringArrays = (
  a: string[],
  b: string[],
  dir: SortDirection = 'asc',
  locale?: string | string[],
  options: Intl.CollatorOptions = { sensitivity: 'base', numeric: true },
) => {
  const collator = new Intl.Collator(locale, options);
  const cmp = a
    .slice(0, Math.min(a.length, b.length))
    .map((av, i) => collator.compare(av, b[i] ?? ''))
    .find((result) => result !== 0);

  if (cmp !== undefined) {
    return dir === 'asc' ? cmp : -cmp;
  }
  const lenCmp = a.length - b.length;

  return dir === 'asc' ? lenCmp : -lenCmp;
};

export const validatePagination = <T, K = undefined>({
  page,
  sort,
  search,
  sortDir,
  filters,
}: SearchRequest<T, K>) => {
  if (typeof page !== 'number' || page < 1) {
    return 'Bad Request: Invalid page number';
  }
  if (sort && typeof sort !== 'string') {
    return 'Bad Request: Invalid sort field';
  }
  if (sortDir && !['asc', 'desc'].includes(sortDir)) {
    return 'Bad Request: Invalid sort direction';
  }
  if (search && typeof search !== 'string') {
    return 'Bad Request: Invalid search query';
  }
  if (filters && typeof filters !== 'object') {
    return 'Bad Request: Invalid filters';
  }

  return undefined;
};

export const sanitizeCompanyName = (): string => {
  const rawName = faker.company.name();
  const regex = /[^\w@#\-()*'" ]/g;

  return rawName.replace(regex, '');
};

export const getPersistSessionObject = <T>(key: string, defaultValue: T): T => {
  const item = getSessionObject<T>(key);

  if (item) {
    return item;
  }
  setSessionObject<T>(key, defaultValue);

  return defaultValue;
};

export const updatePersistSessionObject = <T>(
  key: string,
  updateFn: (currentValue: T) => T,
): T => {
  const currentValue = getPersistSessionObject<T>(key, {} as T);
  const updatedValue = updateFn(currentValue);

  setSessionObject<T>(key, updatedValue);

  return updatedValue;
};

export const setPersistSessionObject = (key: string, value: unknown): void => {
  setSessionObject(key, value);
};

export const getPaginatedData = <T>({
  data,
  page,
  pageSize,
}: {
  data: T[];
  page: number;
  pageSize: number;
}): {
  paginatedData: T[];
  totalPages: number;
  totalEntries: number;
} => {
  const startIndex = (page - 1) * pageSize;
  const paginatedData = data.slice(startIndex, startIndex + pageSize);
  const totalPages = Math.ceil(data.length / pageSize);
  const totalEntries = data.length;

  return {
    paginatedData,
    totalPages,
    totalEntries,
  };
};
